import InspectionStatus from '@/enums/InspectionStatus.enum';
import { Inspection } from '@/types/Inspection.type';
import { isInspectionStatus } from '@/utils/inspectionStart.utils';

/**
 * Status to set when starting: PLANNED or PAUSED (by same user) → STARTED; otherwise unchanged.
 */
export function getStatusForStart(currentStatusId: string): string {
  if (
    isInspectionStatus(currentStatusId, InspectionStatus.PLANNED) ||
    isInspectionStatus(currentStatusId, InspectionStatus.PAUSED)
  ) {
    return InspectionStatus.STARTED;
  }
  return currentStatusId;
}

export type InspectionStartPutBody = {
  inspectionStatusId?: string;
  inspectedBy?: string;
  latitude?: number;
  longitude?: number;
  startTime?: string;
};

type InspectionStartFields = Partial<
  Pick<Inspection, 'startTime' | 'inspectedBy' | 'inspectionStatusId' | 'latitude' | 'longitude'>
>;

export type BuildInspectionStartPutParams = {
  inspection: InspectionStartFields;
  userId: string | undefined;
  isAdministrator: boolean;
  deviceCoords?: { latitude: number; longitude: number } | null;
  nowIso?: string;
};

/**
 * Returns the existing startTime, or now when it has never been set.
 */
export function resolveWriteOnceStartTime(
  existingStartTime: string | undefined | null,
  nowIso: string = new Date().toISOString()
): string {
  if (existingStartTime) {
    return existingStartTime;
  }
  return nowIso;
}

/**
 * Stamps startTime locally only when the inspection has none and the user is not an administrator.
 */
export function withWriteOnceStartTime<T extends object>(
  inspection: T,
  isAdministrator: boolean,
  nowIso?: string
): T & { startTime?: string } {
  const current = inspection as T & { startTime?: string };
  if (isAdministrator || current.startTime) {
    return current;
  }
  return {
    ...current,
    startTime: resolveWriteOnceStartTime(undefined, nowIso),
  };
}

/**
 * Builds the Start Inspection PUT body.
 * Non-administrators always send the start status so the backend records `started_by`
 * in Result_history on every start. startTime is included only on the first start.
 */
export function buildInspectionStartPut(params: BuildInspectionStartPutParams): {
  body: InspectionStartPutBody;
  startTime: string | undefined;
  shouldPut: boolean;
} {
  const { inspection, userId, isAdministrator, deviceCoords = null, nowIso } = params;
  const startTime = isAdministrator
    ? inspection.startTime
    : resolveWriteOnceStartTime(inspection.startTime, nowIso);
  const body: InspectionStartPutBody = {};
  const startStatusId = getStatusForStart(
    inspection.inspectionStatusId ?? InspectionStatus.PLANNED
  );
  if (!isAdministrator && isInspectionStatus(startStatusId, InspectionStatus.STARTED)) {
    body.inspectionStatusId = startStatusId;
  }
  if (!isAdministrator && !inspection.inspectedBy) {
    body.inspectionStatusId = startStatusId;
    body.inspectedBy = userId;
    if ((inspection.latitude == null || inspection.longitude == null) && deviceCoords) {
      body.latitude = deviceCoords.latitude;
      body.longitude = deviceCoords.longitude;
    }
  }
  if (!isAdministrator && !inspection.startTime && startTime) {
    body.startTime = startTime;
  }
  return {
    body,
    startTime,
    shouldPut: Object.keys(body).length > 0,
  };
}
